/**
 *
 * Data model entry point
 *
 * @module DataModel
 *
 * @author Cedric Hoarau <cedric.hoarau@kabandy.com>
 *
 * @copyright (c) Kabandy 2016-2022. All rights reserved.
 *
 */

import { AccessDataModel } from "./Access";
import { AccessPeriodDataModel } from "./AccessPeriod";
import { AccessRoleDataModel } from "./AccessRole";
import { BoardDataModel } from "./Board";
import { BoardTemplateDataModel } from "./BoardTemplate";
import { BoardCopyDataModel } from "./BoardCopy";
import { BoardCellDataModel } from "./BoardCell";
import { BoardCellQuantityDataModel } from "./BoardCellQuantity";
import { BoardCellQuantityRuleDataModel } from "./BoardCellQuantityRule";
import { CommentDataModel } from "./Comment";
import { CustomPropertyDataModel } from "./CustomProperties";
import { ContractDataModel } from "./Contract";
import { ConversationDataModel } from "./Conversation";
import { DocumentDataModel } from "./Document";
import { ExternalCompanyDataModel } from "./ExternalCompany";
import { ExternalUserDataModel } from "./ExternalUser";
import { ModelGroup } from "./ModelGroup";
import { ModelGroupFilters } from "./ModelGroupFilters";
import { PostDataModel } from "./Post";
import { ProjectDataModel } from "./Project";
import { StatusDataModel } from "./Status";
import { UserDataModel } from "./User";
import { OrderByDirection, Timestamp, WhereFilterOp } from "@google-cloud/firestore";
import { QuantityDataModel } from "./Quantity";
import { QuantityRuleDataModel } from "./QuantityRule";
import { SettingDataModel } from "./Setting";
import { ViewpointDataModel } from "./Viewpoint";
import { MeasureDataModel } from "./Measure";
import { NoteDataModel } from "./Note";
import { FileDataModel } from "./File";
import { ModelfileDataModel } from "./Modelfile";
import { ValueDataModel } from "./Value";
import { ModelDataModel } from "./Model";
import { FolderDataModel } from "./Folder";
import { LinkDataModel } from "./Link";

//
// --- Time

declare interface ITime {
    creation: Timestamp;
    lastModified: Timestamp;
}
declare interface ITimeSerialized {
    creation: number;
    lastModified: number;
}

//
// --- Query

declare interface IQueryOrderParam {
    fieldPath: string;
    orderByDirection: OrderByDirection;
}
declare interface IQueryFilterParam {
    fieldPath: string;
    opStr: WhereFilterOp;
    value: any;
}
declare interface IQueryParam {
    order?: IQueryOrderParam[];
    filter?: IQueryFilterParam[];
    limit?: number;
}

//
// --- Order by

const enum EOrderByDirection {
    Ascending = "asc",
    Descending = "desc"
}

//
// --- Section|Group|Item|Property|Value

declare interface IGroup {
    id: string;
    boardId?: string;
    sectionId: string;
    time: any;
    rank?: string;
    content: any;
}
declare interface IItem {
    id: string;
    boardId?: string;
    groupId: string;
    sectionId: string;
    rank?: string;
    time: any;
}
declare interface IProperty {
    id: string;
    boardId?: string;
    rank?: string;
    level: "item" | "group" | "section";
    content?: {
        name: string;
        type: string; //string as defined in BoardUtils/BoardConfig.PropertyType
        units?: string;
        operation?: {
            //mandatory only for formula type
            properties: string[];
            formula: string;
        };
        orderItems?: []; //mandatory for list and status type
        items?: {
            //mandatory for list and status type
            [id: string]: {
                id: string;
                name: string;
                color?: string; //mandatory for status type
            };
        };
    };
    time?: any;
}
declare interface ISection {
    id: string;
    boardId?: string;
    rank?: string;
    content: any;
    time: any;
}
declare interface IValue {
    id?: string;
    boardId?: string;
    parameterId: string;
    propertyId: string;
    level: "item" | "group" | "section";
    content: any;
    time?: any;
}

// TODO: move to BoardValue.ts
declare interface IBoardValueGetInfo {
    projectId: string;
    boardId: string;
    id: string;
}

//
// --- Notification

declare interface INotifUserInfo {
    id: string;
    name: string;
}
declare interface IGenericNotification {
    id: string;
    userId: string;
    userSrc: INotifUserInfo;
    read: boolean;
    time: ITime | ITimeSerialized;
    targetUrl?: string;
    content?: any;
}
declare interface INotification extends IGenericNotification {
    time: ITime;
}
declare interface INotificationSerialized extends IGenericNotification {
    time: ITimeSerialized;
}
declare interface INotifCreateInfo {
    userId: string;
    userSrc: INotifUserInfo;
    read: boolean;
    targetUrl?: string;
    content?: any;
}
declare interface INotifDeleteInfo {
    userId: string;
    notificationId: string;
}
declare interface INotifGetInfo {
    userId: string;
    notificationId: string;
}
declare interface INotifGetByQueryInfo {
    userId: string;
    queryParam: IQueryParam;
}
declare interface INotifUpdateInfo {
    userId: string;
    notificationId: string;
    read?: boolean;
    content?: any;
}

//
// --- Notification box

declare interface INotificationBox {
    id: string;
    userId: string;
    lastCheckTime: Timestamp;
    time: ITime;
    content?: any;
}
declare interface INotifBoxCreateInfo {
    userId: string;
    content?: any;
}
declare interface INotifBoxDeleteInfo {
    userId: string;
}
declare interface INotifBoxGetInfo {
    userId: string;
}
declare interface INotifBoxUpdateInfo {
    userId: string;
    lastCheckTime?: any;
    content?: any;
}

//
// --- Snapshot

const enum ESnapshotTargetType {
    Model = "model",
    ModelGroup = "modelgroup"
}
declare interface ISnapshot {
    id: string; // modelgroupId|modelId
    storageId: string; // uuid on storage
    projectId: string;
    userId: string;
    targetType: ESnapshotTargetType;
    time: ITime | ITimeSerialized;
    content?: any;
}
declare interface ISnapshotCreateInfo {
    projectId: string;
    snapshotId: string; // modelgroupId|modelId
    userId: string;
    storageId: string; // uuid on storage
    targetType: string;
    content?: any;
}
declare interface ISnapshotDeleteInfo {
    projectId: string;
    snapshotId: string;
}
declare interface ISnapshotGetInfo {
    projectId: string;
    snapshotId: string;
}
declare interface ISnapshotUpdateInfo {
    projectId: string;
    snapshotId: string;
    userId: string;
    storageId: string;
    content?: any;
}

declare interface IIdsMapping {
    [key: string]: string;
}

declare interface IEntityMapping<T extends { id: string }> {
    [id: string]: T;
}

const enum ECopyVersion {
    V1 = 1
}

export {
    AccessDataModel,
    AccessPeriodDataModel,
    AccessRoleDataModel,
    BoardCellDataModel,
    BoardCellQuantityDataModel,
    BoardCellQuantityRuleDataModel,
    BoardDataModel,
    BoardTemplateDataModel,
    BoardCopyDataModel,
    CommentDataModel,
    CustomPropertyDataModel,
    ConversationDataModel,
    DocumentDataModel,
    ExternalUserDataModel,
    ExternalCompanyDataModel,
    PostDataModel,
    ProjectDataModel,
    QuantityDataModel,
    QuantityRuleDataModel,
    SettingDataModel,
    StatusDataModel,
    UserDataModel,
    ViewpointDataModel,
    MeasureDataModel,
    NoteDataModel,
    FileDataModel,
    ModelfileDataModel,
    EOrderByDirection,
    ESnapshotTargetType,
    IBoardValueGetInfo,
    IGroup,
    IItem,
    INotifBoxCreateInfo,
    INotifBoxDeleteInfo,
    INotifBoxGetInfo,
    INotifBoxUpdateInfo,
    INotifCreateInfo,
    INotifDeleteInfo,
    INotifGetInfo,
    INotifGetByQueryInfo,
    INotification,
    INotificationSerialized,
    INotificationBox,
    INotifUpdateInfo,
    INotifUserInfo,
    IProperty,
    IQueryParam,
    IQueryFilterParam,
    ISection,
    ISnapshot,
    ISnapshotCreateInfo,
    ISnapshotDeleteInfo,
    ISnapshotGetInfo,
    ISnapshotUpdateInfo,
    ITime,
    ITimeSerialized,
    IValue,
    IQueryOrderParam,
    ModelGroup,
    ModelGroupFilters,
    ValueDataModel,
    ModelDataModel,
    IIdsMapping,
    IEntityMapping,
    FolderDataModel,
    LinkDataModel,
    ECopyVersion,
    ContractDataModel
};
